import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitCommitHorizontal, RefreshCw, Undo2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { gitApi } from "@/api/browser.ts";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Input, Select, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { GithubPublish } from "./GithubPublish.tsx";

/** Git history: repos, commits, structured diff, restore points and detached checkout. */
export function GitPanel({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const repos = useQuery({ queryKey: ["git", projectId, "repos"], queryFn: () => gitApi.repos(projectId) });
  const [repo, setRepo] = useState<string | null>(null);
  const current = repo ?? repos.data?.[0]?.path ?? null;
  const log = useQuery({ queryKey: ["git", projectId, "log", current], queryFn: () => gitApi.log(projectId, current!), enabled: current !== null });
  const [sha, setSha] = useState<string | null>(null);
  const detail = useQuery({ queryKey: ["git", projectId, "commit", current, sha], queryFn: () => gitApi.commit(projectId, current!, sha!), enabled: !!sha && current !== null });
  const [msg, setMsg] = useState("");
  const [checkpointMsg, setCheckpointMsg] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["git", projectId] });
  const init = useMutation({ mutationFn: () => gitApi.init(projectId, ""), onSuccess: refresh });
  const checkpoint = useMutation({ mutationFn: () => gitApi.checkpoint(projectId, current!, msg), onSuccess: () => { setMsg(""); void refresh(); } });
  const checkout = useMutation({ mutationFn: (s: string) => gitApi.checkout(projectId, current!, s, checkpointMsg || undefined), onSuccess: () => { setCheckpointMsg(""); void refresh(); } });
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  const repoInfo = repos.data?.find((r) => r.path === current);
  const err = init.error ?? checkpoint.error ?? checkout.error;

  if (repos.isLoading) return <div className="p-3"><Spinner /></div>;
  if (repos.data?.length === 0)
    return (
      <div className="space-y-3 text-sm">
        <GithubPublish projectId={projectId} canEdit={canEdit} />
        <p className="px-3 text-fg-muted">{t("git.noRepo")}</p>
        {canEdit && <div className="px-3"><Button size="sm" onClick={() => init.mutate()} loading={init.isPending}>{t("git.init")}</Button></div>}
        {err && <div className="px-3"><Alert tone="danger">{toMsg(err)}</Alert></div>}
      </div>
    );

  return (
    <div className="flex h-full flex-col text-sm">
      <GithubPublish projectId={projectId} canEdit={canEdit} />
      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
        <Select className="h-8 flex-1 text-xs" value={current ?? ""} onChange={(e) => { setRepo(e.target.value); setSha(null); }}>
          {(repos.data ?? []).map((r) => <option key={r.path} value={r.path}>{r.path || "/workspace"}{r.branch ? ` · ${r.branch}` : ""}{r.dirty ? ` · ${t("git.dirty")}` : ""}</option>)}
        </Select>
        <button type="button" className="rounded p-1 text-fg-muted hover:text-fg" aria-label={t("git.refresh")} onClick={() => void refresh()}><RefreshCw className="size-4" /></button>
      </div>
      {canEdit && (
        <form className="flex items-center gap-2 border-b border-border px-2 py-1.5" onSubmit={(e) => { e.preventDefault(); if (msg.trim()) checkpoint.mutate(); }}>
          <Input className="h-8 flex-1 text-xs" placeholder={repoInfo?.dirty ? t("git.checkpointPlaceholder") : t("git.clean")} disabled={!repoInfo?.dirty} value={msg} onChange={(e) => setMsg(e.target.value)} />
          <Button type="submit" size="sm" disabled={!repoInfo?.dirty || !msg.trim()} loading={checkpoint.isPending}><GitCommitHorizontal className="size-3.5" /> {t("git.checkpoint")}</Button>
        </form>
      )}
      {err && <div className="px-2 pt-2"><Alert tone="danger">{toMsg(err)}</Alert></div>}
      <div className={cn("grid min-h-0 flex-1", sha ? "grid-rows-[minmax(0,1fr)_minmax(0,2fr)]" : "")}>
        <ul className="min-h-0 overflow-auto p-1">
          {log.data?.length === 0 && <li className="p-2 text-xs text-fg-muted">{t("git.noCommits")}</li>}
          {(log.data ?? []).map((c) => (
            <li key={c.sha}>
              <button type="button" onClick={() => setSha(c.sha)} className={cn("flex w-full flex-col items-start rounded px-2 py-1 text-start hover:bg-surface-2", sha === c.sha && "bg-accent-soft")}>
                <span className="flex w-full items-center gap-2 text-xs"><span dir="ltr" className="font-mono text-fg-muted">{c.short}</span><span className="truncate">{c.subject}</span>{c.current && <span className="ms-auto rounded bg-success-soft px-1 text-[10px] text-success">HEAD</span>}</span>
                <span className="text-[10px] text-fg-muted">{c.author} · {fmt(c.date)}</span>
              </button>
            </li>
          ))}
        </ul>
        {sha && (
          <div className="flex min-h-0 flex-col border-t border-border">
            <div className="flex items-center gap-2 border-b border-border px-2 py-1 text-xs">
              <span dir="ltr" className="font-mono text-fg-muted">{detail.data?.commit.short ?? sha.slice(0, 7)}</span>
              <span className="min-w-0 flex-1 truncate">{detail.data?.commit.subject}</span>
              {canEdit && detail.data && !detail.data.commit.current && (
                <>
                  {repoInfo?.dirty && <Input className="h-7 w-40 text-xs" placeholder={t("git.checkpointBefore")} value={checkpointMsg} onChange={(e) => setCheckpointMsg(e.target.value)} />}
                  <Button size="sm" variant="secondary" disabled={!!repoInfo?.dirty && !checkpointMsg.trim()} loading={checkout.isPending} onClick={() => checkout.mutate(sha)} title={t("git.switchHint")}><Undo2 className="size-3.5" /> {t("git.switch")}</Button>
                </>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-auto bg-bg" dir="ltr">
              {detail.isLoading && <div className="p-3"><Spinner /></div>}
              {detail.data?.body && <p className="whitespace-pre-wrap border-b border-border p-2 text-xs text-fg-muted">{detail.data.body}</p>}
              {detail.data?.files.length === 0 && <p className="p-3 text-xs text-fg-muted">{t("git.noChanges")}</p>}
              {detail.data?.files.map((f) => (
                <details key={f.path} open={detail.data!.files.length <= 5} className="border-b border-border">
                  <summary className="flex cursor-pointer items-center gap-2 px-2 py-1 text-xs"><span className="min-w-0 flex-1 truncate font-mono">{f.path}</span><span className="text-success">+{f.additions}</span><span className="text-danger">-{f.deletions}</span></summary>
                  <pre className="overflow-x-auto p-2 font-mono text-[11px] leading-5">
                    {f.patch.split("\n").map((l, i) => (
                      <div key={i} className={cn(l.startsWith("+") && !l.startsWith("+++") ? "bg-success-soft/50 text-success" : l.startsWith("-") && !l.startsWith("---") ? "bg-danger-soft/50 text-danger" : l.startsWith("@@") ? "text-accent" : "text-fg-muted")}>{l}</div>
                    ))}
                    {f.truncated && <div className="text-warn">…[{t("files.truncated")}]</div>}
                  </pre>
                </details>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
